import React from "react";
import { PAYOR_LOGOS } from "../lib/site-data";
import { LogoMarquee } from "./LogoMarquee";
import { CheckCircle2 } from "lucide-react";

const BULLETS = [
  "Enrollment with insurance panels — Medicare, Medicaid, and commercial payers",
  "CAQH profile setup, attestation, and continuous maintenance",
  "Re-credentialing cycles managed before deadlines expire",
  "Facility credentialing for clinics, hospitals, and group practices",
  "Individual provider credentialing for MDs, DOs, NPs, PAs, BCBAs, LCSWs",
  "Typical timelines: 60–120 days depending on payer & state",
];

export const Credentialing = () => (
  <section
    id="credentialing"
    data-testid="credentialing-section"
    className="py-20 md:py-28 bg-[color:var(--brand-green-50)]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8">
      <div className="grid grid-cols-12 gap-y-10 lg:gap-10 items-start">
        <div className="col-span-12 lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[color:var(--brand-green-200)] text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
            Credentialing
          </span>
          <h2 className="font-display mt-5 text-4xl md:text-5xl font-bold text-navy">
            Provider & facility credentialing — done right, on time.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            We handle the paperwork so you can focus on patients. Our credentialing team enrolls
            individual providers and healthcare facilities with major insurance panels, manages
            CAQH profiles, and keeps re-credentialing on schedule.
          </p>

          <ul className="mt-8 space-y-3">
            {BULLETS.map((b, i) => (
              <li
                key={b}
                data-testid={`credentialing-bullet-${i}`}
                className="flex items-start gap-3 text-[15px] text-slate-700"
              >
                <CheckCircle2 className="w-5 h-5 text-[color:var(--brand-green-700)] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 lg:col-span-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--brand-green-700)] font-bold">
              Major Payers We Enroll With
            </p>
            <h3 className="font-display text-2xl text-navy mt-2">
              A trusted liaison to the nation's largest insurance networks.
            </h3>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PAYOR_LOGOS.map((p, i) => (
                <div
                  key={p.name}
                  data-testid={`payor-${i}`}
                  className="flex items-center justify-center py-4 px-3 bg-white border border-[color:var(--brand-green-200)] rounded-xl h-20"
                  title={p.name}
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className="h-10 w-auto object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.outerHTML = `<span class="text-[11px] font-semibold text-slate-700 text-center leading-tight">${p.name}</span>`;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--brand-green-700)] font-bold text-center">
          Payer network partners
        </p>
        <LogoMarquee logos={PAYOR_LOGOS} testIdPrefix="payor-marquee" itemHeight="h-12" />
      </div>
    </div>
  </section>
);
